import React, { useRef, useEffect } from 'react'
import { useTheme } from 'contexts/theme'
import { useModal } from 'modals/dialog'
import { pick } from 'utilities/object'
import Popover from 'components/popover'
import { Widget } from './s'

const FieldRenderer = ({ children, content, ...props }) => {
    const popoverProps = pick(props, 'placement', 'trigger', 'callback', 'show', 'className')

    return (
        <Popover
            content={content}
            className="widget"
            {...popoverProps}>
            {children}
        </Popover>
    )
}

const FallbackFieldRenderer = ({ children, content, control: controlRef, show, hide, salt, ...props }) => {
    const modal = useModal()

    const widgetRef = useRef()

    const widgetProps = pick(props, 'scrollable')

    const position = {
        origin: controlRef,
        direction: {
            x: {
                where: 'inside',
                to: 'right'
            },
            y: {
                where: 'outside',
                to: 'down',
                adjust: 8
            }
        }
    }

    const updatePosition = node => {
        if(controlRef?.current) {
            const { top, right, bottom, left } = controlRef.current.getBoundingClientRect()
            const { scrollY } = global

            // For x-axis positioning (horizontal)
            if(position.direction.x.where === 'inside') {
                if(position.direction.x.to === 'right') {
                    node.style.setProperty('--__huma-widget-left', `${left}px`)
                } else if(position.direction.x.to === 'left') {
                    node.style.setProperty('--__huma-widget-right', `${right}px`)
                }
            } else {
                if(position.direction.x.to === 'right') {
                    node.style.setProperty('--__huma-widget-left', `${right}px`)
                } else if(position.direction.x.to === 'left') {
                    node.style.setProperty('--__huma-widget-right', `${left}px`)
                }
            }

            // For y-axis positioning (vertical)
            if(position.direction.y.where === 'inside') {
                if(position.direction.y.to === 'down') {
                    node.style.setProperty('--__huma-widget-top', `${top + scrollY}px`)
                } else if(position.direction.y.to === 'up') {
                    node.style.setProperty('--__huma-widget-bottom', `${bottom + scrollY}px`)
                }
            } else {
                if(position.direction.y.to === 'down') {
                    node.style.setProperty('--__huma-widget-top', `${bottom + scrollY}px`)
                } else if(position.direction.y.to === 'up') {
                    node.style.setProperty('--__huma-widget-bottom', `${top + scrollY}px`)
                }
            }
        }
    }

    useEffect(() => {
        if(widgetRef.current) {
            const supportsPopover = Object.prototype.hasOwnProperty.call(HTMLElement.prototype, 'popover')

            if(show && supportsPopover) {
                // Update position while widget is still hidden
                // Needed since anchor positioning is not supported yet and therefore the inset values will be wrong
                updatePosition(widgetRef.current)

                // Show the popover (just to have the component end up in the top layer)
                requestAnimationFrame(() => {
                    try {
                        widgetRef.current?.setAttribute('popover', 'auto')
                        widgetRef.current?.showPopover?.()
                    } catch {
                        widgetRef.current.style.display = 'block'
                    }
                })
            }
        }
    }, [show])

    return (
        <>
            {children}
            <Widget
                show={show}
                clickOutside={{
                    inside: [controlRef],
                    action: hide
                }}
                position={position}
                closeButton={false}
                constrain={true}
                {...(modal?.ref?.current ? { appendTo: modal.ref.current } : null)}
                salt={salt}
                ref={widgetRef}
                {...widgetProps}>
                {content}
            </Widget>
        </>
    )
}

export default props => {
    const { supportsAnchorPositioning } = useTheme()

    if(!!supportsAnchorPositioning) {
        return <FieldRenderer {...props} />
    }

    return <FallbackFieldRenderer {...props} />
}